import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { referralAPI, clientAPI } from '../services/api';
import PageHeader from './PageHeader';
import { hasPermission } from '../utils/permissions';
import { formatDisplayDate } from '../utils/dateFormat';
import './ReferralList.css';

function ReferralList() {
  const params = useParams();
  const navigate = useNavigate();
  const { tenantPath } = useTenantFromUrl();
  const clientId = params.clientId || params.id;
  const [referrals, setReferrals] = useState([]);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (clientId) {
      loadClient();
      loadReferrals();
    }
  }, [clientId]);

  const loadClient = async () => {
    try {
      const response = await clientAPI.getById(clientId);
      setClient(response.data.data);
    } catch (error) {
      console.error('Error loading client:', error);
    }
  };

  const loadReferrals = async () => {
    try {
      setLoading(true);
      const response = await referralAPI.getByClientId(clientId);
      setReferrals(response.data.data || []);
    } catch (error) {
      console.error('Error loading referrals:', error);
      alert('Failed to load referrals');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => (dateString ? formatDisplayDate(dateString) : 'N/A');

  const isNestedUnderClient = !!params.id && !params.clientId;
  const handleNewReferral = () => {
    navigate(tenantPath(isNestedUnderClient ? `/client/${clientId}/referral/new` : `/referral/new?clientId=${clientId}`));
  };

  const handleViewReferral = (referralId) => {
    navigate(tenantPath(isNestedUnderClient ? `/client/${clientId}/referral/${referralId}` : `/referral/${referralId}`));
  };

  if (loading) {
    return <div className="loading">Loading referrals...</div>;
  }

  const canCreateReferral = hasPermission('referrals.create');

  return (
    <div className="referral-list">
      <div className="header">
        <PageHeader
          title={client ? `Referrals for ${client.last_name}, ${client.first_name}` : 'Referrals'}
        >
          {canCreateReferral && (
            <button
              onClick={handleNewReferral}
              className="btn-new"
            >
              + New Referral
            </button>
          )}
        </PageHeader>
      </div>

      <div className="referrals-table">
        <table>
          <thead>
            <tr>
              <th>Referral Date</th>
              <th>Open Date</th>
              <th>Close Date</th>
              <th>Referral source</th>
              <th>Status</th>
              <th>Intake Staff</th>
              <th className="referrals-col-tracking">Tracking</th>
            </tr>
          </thead>
          <tbody>
            {referrals.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-data">
                  No referrals found for this client.
                  <button onClick={handleNewReferral} className="btn-link">
                    Create the first referral
                  </button>
                </td>
              </tr>
            ) : (
              referrals.map((referral) => (
                <tr
                  key={referral.id}
                  className={referral.is_current && !referral.close_date ? 'current-referral' : ''}
                  onClick={() => handleViewReferral(referral.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleViewReferral(referral.id); } }}
                  aria-label={`View referral ${formatDate(referral.open_date)}`}
                >
                  <td>{formatDate(referral.referral_date)}</td>
                  <td>{formatDate(referral.open_date)}</td>
                  <td>{formatDate(referral.close_date)}</td>
                  <td>{(referral.referral_level_1_description && referral.referral_level_2_description) ? `${referral.referral_level_1_description} / ${referral.referral_level_2_description}` : (referral.referral_level_1_description || referral.referral_level_2_description || 'N/A')}</td>
                  <td className="status-cell">
                    {referral.close_date ? (
                      <span className="status-text status-closed">Closed</span>
                    ) : referral.is_current ? (
                      <span className="status-text status-current">Current</span>
                    ) : (
                      <span className="status-text status-inactive">Inactive</span>
                    )}
                  </td>
                  <td>
                    {referral.intake_staff_first_name && referral.intake_staff_last_name
                      ? `${referral.intake_staff_first_name} ${referral.intake_staff_last_name}`
                      : 'N/A'}
                  </td>
                  <td className="referrals-cell-tracking">
                    <Link
                      to={tenantPath(`/client/${clientId}/milestones?referralId=${encodeURIComponent(referral.id)}`)}
                      className="referrals-tracking-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Client Tracking
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ReferralList;
